import { find } from 'lodash';
import { getUserAchievements, getUserEmploymentPendingQuestions, getUserProofRequests } from '../commands/userCommands';
import { auth } from '../data-types/initialisation/initialisationConstants';
import { SignupCheckpoint } from '../data-types/signup/signupInterfaces';
import { SAFER_GAMBLING_QUESTION_TYPES } from '../data-types/user/userConstants';
import { UserQuestionsTypes } from '../data-types/user/userEnums';
import { BasicUserInformation, PendingQuestion } from '../data-types/user/userInterfaces';
import { setCountrySettingsByLocation } from '../state/actions/customizationActions';
import {
  setLoadingAchievements,
  setPendingQuestions,
  setProofRequests,
  setSaferGamblingQuestionsSetType,
  setUserAchievements,
  setUserAchievementsUpdated,
  setUserDetails,
} from '../state/actions/userDataActions';
import { store } from '../state/store';
import { anyAchievementsUpdated } from './achievements';
import { orderQuestionsByRequestedAtDesc } from './profile';

export const setCountrySettingsByUserLocation = () => {
  const {
    userData: { userLocation },
    customization: { internationalSettings },
  } = store.getState();
  const { allAvailableCountrySettings } = internationalSettings;

  const searchedCountrySettings = find(allAvailableCountrySettings, { isoA2: userLocation });
  setCountrySettingsByLocation(searchedCountrySettings);
};

export const isGhostUser = () => {
  const {
    userData: { authentication },
  } = store.getState();

  return authentication === auth.UNAUTHENTICATED;
};

export const hasMadeAFirstDeposit = () => {
  const {
    userData: { user },
  } = store.getState();

  return user && user.firstDepositAt !== undefined;
};

export const checkUserAuthenticationLevel = (status: number) => {
  const {
    userData: { authentication },
  } = store.getState();

  return authentication === status;
};

export const blurEmail = (email: string) => {
  const atIndex = email.indexOf('@');
  const finishBlurIndex = atIndex - 1;
  let stars = '';
  for (let i = 1; i < finishBlurIndex; i += 1) {
    stars += '*';
  }

  return `${email[0]}${stars}${email.substring(finishBlurIndex)}`;
};

export const blurPhoneNumber = (phoneNumber: string) => {
  const noOfDiscoveredLastDigits = 4;
  const endIndex = phoneNumber.length - noOfDiscoveredLastDigits;
  const startsWithPlus = phoneNumber.startsWith('+');
  const startIndex = startsWithPlus ? 2 : 1;
  let stars = '';
  for (let i = startIndex; i < endIndex; i += 1) {
    stars += '*';
  }

  return `${phoneNumber.substring(0, startIndex)}${stars}${phoneNumber.substring(endIndex)}`;
};

export const adjustAcknowledgeDepositFlagIfNeeded = (checkpoint: SignupCheckpoint, acknowledgeDepositAt: string | undefined) => {
  if (checkpoint && checkpoint.templateOptions && checkpoint.templateOptions.acknowledgeDepositInfoNeeded && !!acknowledgeDepositAt) {
    checkpoint.templateOptions.acknowledgeDepositInfoNeeded = false;
  }

  return checkpoint;
};

export const setBasicUserInformation = (user: BasicUserInformation) => {
  setUserDetails({
    id: user.id || user.userId,
    acceptedTermsId: user.acceptedTermsId,
    email: user.email,
    firstDepositAt: user.firstDepositAt, // Date technically
    fractionalOdds: !!user.fractionalOdds,
    oddsTypeId: user.oddsTypeId,
    status: user.status,
  });
};

export const loadUserProofRequests = async () => {
  try {
    const proofRequests = await getUserProofRequests();

    setProofRequests(proofRequests);
  } catch (e) {
    setProofRequests([]);
  }
};

const loadSaferGamblingQuestions = async () => {
  try {
    const latestQuestionsAndAnswers: PendingQuestion[] = [];
    for (const type of SAFER_GAMBLING_QUESTION_TYPES) {
      const questionsSet = await getUserEmploymentPendingQuestions(type);

      if (questionsSet && questionsSet.length && questionsSet[0]) {
        latestQuestionsAndAnswers.push(questionsSet[0]);
      }
    }

    const orderedQuestions = orderQuestionsByRequestedAtDesc(latestQuestionsAndAnswers);
    if (orderedQuestions && orderedQuestions.length && !orderedQuestions[0].answers) {
      setSaferGamblingQuestionsSetType(orderedQuestions[0].type);
    }
  } catch (e) {
    setSaferGamblingQuestionsSetType(undefined);
  }
};

const loadCDDPendingQuestions = async () => {
  try {
    const pendingQuestions = await getUserEmploymentPendingQuestions();
    if (pendingQuestions && pendingQuestions.find(question => question.type === UserQuestionsTypes.CDD && question.requestedAt)) {
      setPendingQuestions(true);
    }
  } catch (e) {
    setPendingQuestions(false);
  }
};

export const loadUserAchievement = async () => {
  await setLoadingAchievements(true);
  try {
    const achievements = await getUserAchievements();
    setUserAchievements(achievements);
    const achievementsUpdated = anyAchievementsUpdated(achievements);
    setUserAchievementsUpdated(achievementsUpdated);
  } catch (e) {
    setUserAchievements([]);
    setUserAchievementsUpdated(false);
  } finally {
    setLoadingAchievements(false);
  }
};

export const loadUserPendingQuestions = () => {
  loadCDDPendingQuestions();
  loadSaferGamblingQuestions();
};
